import { useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { FormTemplate, useFormContext } from 'framework/form';
import type { FormFieldsData, FormTemplateProps } from 'framework/form';
import { SideDrawer } from 'framework/ui/mui-builder';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { mergeSx } from 'framework/utils';
import CreateEditFormContentLayout, { CreateEditFormSection } from './CreateEditFormContentLayout';

type CreateEditFormDrawerContentProps = {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  getSections: (data: FormFieldsData) => CreateEditFormSection[];
  width?: number | string;
  header?: string;
  submitButtonTitle?: string | ((data: FormFieldsData) => string);
  secondaryButtonTitle?: string;
  onSecondaryAction?: (data: FormFieldsData) => void | Promise<void>;
  sx?: SxProps<Theme>;
};

export type CreateEditFormDrawerProps = Omit<FormTemplateProps, 'children'> & CreateEditFormDrawerContentProps;

const formSx: SxProps<Theme> = {
  p: 0,
  overflow: 'visible',
  '& .floating-label-input, & .floating-label-baseInput, & .MuiFormControl-root': { mb: 0 },
  '& .form-group .MuiFormGroup-root': { minHeight: 0 },
  '& .form-group .MuiRadio-root': { my: '-9px' },
};

const CreateEditFormDrawerContent = ({
  isOpen,
  onClose,
  title,
  getSections,
  width,
  header,
  submitButtonTitle,
  secondaryButtonTitle,
  onSecondaryAction,
  sx,
}: CreateEditFormDrawerContentProps) => {
  const { data, submitForm, isSubmitEnabled, isLoadingArtifact } = useFormContext();
  const { addMessage } = useMessageService();
  const [isSecondaryActionPending, setSecondaryActionPending] = useState(false);
  const secondaryActionPendingRef = useRef(false);
  const isPending = !isSubmitEnabled || Boolean(isLoadingArtifact) || isSecondaryActionPending;

  const handleSecondaryAction = async () => {
    if (!onSecondaryAction || isPending || secondaryActionPendingRef.current) return;

    secondaryActionPendingRef.current = true;
    setSecondaryActionPending(true);

    try {
      await onSecondaryAction(data ?? {});
    } catch {
      addMessage(new ErrorMessage('Could not complete the action. Please try again.'));
    } finally {
      secondaryActionPendingRef.current = false;
      setSecondaryActionPending(false);
    }
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={() => {
        if (!isPending && !secondaryActionPendingRef.current) onClose();
      }}
      title={title}
      width={width}
      content={
        <CreateEditFormContentLayout
          header={header}
          sections={getSections(data ?? {})}
          submitButton={{
            title:
              (typeof submitButtonTitle === 'function' ? submitButtonTitle(data ?? {}) : submitButtonTitle) ?? 'Save',
            align: 'left',
            onSubmit: () => {
              if (!isPending && !secondaryActionPendingRef.current) submitForm?.();
            },
            disabled: isPending,
          }}
          secondaryButton={
            onSecondaryAction
              ? {
                  title: secondaryButtonTitle,
                  onClick: handleSecondaryAction,
                  disabled: isPending,
                }
              : undefined
          }
          sx={mergeSx(formSx, sx)}
        />
      }
    />
  );
};

const CreateEditFormDrawer = ({
  isOpen,
  onClose,
  title,
  getSections,
  width,
  header,
  submitButtonTitle,
  secondaryButtonTitle,
  onSecondaryAction,
  sx,
  ...formProps
}: CreateEditFormDrawerProps) => (
  <FormTemplate {...formProps}>
    <CreateEditFormDrawerContent
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      getSections={getSections}
      width={width}
      header={header}
      submitButtonTitle={submitButtonTitle}
      secondaryButtonTitle={secondaryButtonTitle}
      onSecondaryAction={onSecondaryAction}
      sx={sx}
    />
  </FormTemplate>
);

export default CreateEditFormDrawer;
